import { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { IBanner } from '../../utils/entities/interface';
import {
  fetchBanners,
  saveBanner,
  editBanner,
  deleteBanner,
} from '../../services/banner';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import { ImageUpload } from '../../components/atoms/UploadImage';
import useSnackbar from '../../hooks/useSnackbar';
import HomeConfigManager from './HomeConfigManager';

const PURPLE = '#7B1FA2';
const PURPLE_DARK = '#4A0E63';
const PURPLE_LIGHT = '#B36FD1';
const SURFACE = '#1C1C1C';
const SURFACE_ALT = '#232323';
const BORDER = '#2A2A2A';
const MUTED = '#8A8D91';

// Wraps a page section with a thin gradient accent along the top edge - the
// one recurring "premium" touch used across every section on this page.
const SectionCard = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}) => (
  <Box
    sx={{
      position: 'relative',
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 3,
      p: 2.5,
      mb: 3,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${PURPLE}, transparent 85%)`,
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// folderType 7 = "homebanners" in nouvakserverapp/src/Utils/common.js -
// the same S3 folder every other homebanners photo (seeded or admin-uploaded)
// lives in. This page used to pass folderType 1 ("service/banner"), a
// different folder from where these images actually belong.
const BANNER_FOLDER_TYPE = 7;

// Mirrors HomeBanner.destination exactly (nouvakserverapp/src/models/HomeBanner/homebanner.model.js).
// The previous 'AD' option here was not a valid enum value at all, so saving
// with it selected would fail backend validation.
const DESTINATION_OPTIONS: { value: IBanner['destination']; label: string }[] = [
  { value: 'ADVERTISE', label: 'Advertise' },
  { value: 'COUPON', label: 'Coupon' },
  { value: 'HOME', label: 'Home' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'HOW_IT_WORK', label: 'How It Works' },
  { value: 'STERILIZATION', label: 'Sterilization' },
];

const SECTION_BY_INDEX: Array<'TOP' | 'MIDDLE' | 'BOTTOM'> = ['TOP', 'MIDDLE', 'BOTTOM'];
const PRIMARY_BANNER_LABELS = ['Top Banner', 'Middle Banner', 'Bottom Banner'];

// Every named, screen-specific banner slot the customer app actually reads
// (see useBannerMap()/bannerSrc() call sites in nouvakcustomerapp), grouped by
// screen so each one always shows up below as its own upload+preview card -
// no "Add Banner" hunting or knowing the section code required. The backend
// HomeBanner.section field is a free string (see homebanner.model.js), so a
// new slot only needs adding to this list, no migration.
const SCREEN_BANNER_GROUPS: { title: string; slots: { section: string; label: string }[] }[] = [
  {
    title: 'Home Screen',
    slots: [{ section: 'EXTRA', label: 'Extra Image' }],
  },
  {
    title: 'Shop Screen',
    slots: [
      { section: 'PROMO', label: 'Promo Ticket' },
      { section: 'SHOP_BIG_BRAND_BASH', label: 'Big Brand Bash' },
      { section: 'SHOP_LAUNCH_DEAL', label: 'Launch Deal' },
      { section: 'SHOP_DISCOUNT_10', label: '10% Discount' },
      { section: 'SHOP_OFFER_1', label: 'Offer Tile 1' },
      { section: 'SHOP_OFFER_2', label: 'Offer Tile 2' },
      { section: 'SHOP_OFFER_3', label: 'Offer Tile 3' },
      { section: 'SHOP_OFFER_4', label: 'Offer Tile 4' },
      { section: 'SHOP_BOTTOM_PROMO', label: 'Bottom Promo' },
    ],
  },
  {
    title: 'All Products Screen',
    slots: [
      { section: 'PRODUCTS_FLAT5_OFF', label: 'Flat 5% Off' },
      { section: 'PRODUCTS_AC_ACCESSORIES', label: 'AC Accessories Tile' },
      { section: 'PRODUCTS_AIR_CONDITIONERS', label: 'Air Conditioners Tile' },
      { section: 'PRODUCTS_GRANT_PROMO', label: 'Grant Promo (also on Product Detail)' },
    ],
  },
];

const NAMED_SLOT_SECTIONS = new Set(
  SCREEN_BANNER_GROUPS.flatMap((g) => g.slots.map((s) => s.section)),
);

// Each named slot gets its own distinct position (100+) instead of a shared
// constant - a shared position for every one of these would let one of them
// tie with (and potentially win) the primary gallery's `slice(0, 3)`
// position-ascending lookup whenever fewer than 3 primary banners exist yet.
const NAMED_SLOT_POSITIONS: Record<string, number> = Object.fromEntries(
  SCREEN_BANNER_GROUPS.flatMap((g) => g.slots).map((s, i) => [s.section, 100 + i]),
);

const emptyNamedSlotBanner = (section: string): IBanner => ({
  appType: 'USER',
  mediaType: 'IMAGE',
  mediaUrl: '',
  destination: 'ADVERTISE',
  data: '',
  position: NAMED_SLOT_POSITIONS[section] ?? 100,
  section,
  isActive: true,
});

// Curated dropdown of known HomeBanner.section values, for the freeform
// "Additional Banners" section below (any future slot not yet in
// SCREEN_BANNER_GROUPS still gets a home there via manual Add Banner).
const KNOWN_BANNER_SECTIONS: { value: string; label: string }[] = [
  { value: 'TOP', label: 'Top (Home)' },
  { value: 'MIDDLE', label: 'Middle (Home)' },
  { value: 'BOTTOM', label: 'Bottom (Home)' },
  ...SCREEN_BANNER_GROUPS.flatMap((g) =>
    g.slots.map((s) => ({ value: s.section, label: `${s.label} (${g.title})` })),
  ),
];

const DEFAULT_BANNER: IBanner = {
  appType: 'USER',
  mediaType: 'IMAGE',
  mediaUrl: '',
  destination: 'ADVERTISE',
  data: '',
  position: 1,
  section: 'TOP',
};

const emptyExtraBanner = (nextPosition: number): IBanner => ({
  appType: 'USER',
  mediaType: 'IMAGE',
  mediaUrl: '',
  destination: 'ADVERTISE',
  data: '',
  position: nextPosition,
  section: 'TOP',
  isActive: true,
});

const SectionHeader = ({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      mb: 2,
      flexWrap: 'wrap',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DARK})`,
          boxShadow: `0 4px 14px rgba(123,31,162,0.4)`,
          color: '#fff',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
          {title}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{subtitle}</Typography>
      </Box>
    </Box>
    {action}
  </Box>
);

const HomeBanner = () => {
  const [banners, setBanners] = useState<IBanner[]>(
    Array(3)
      .fill(null)
      .map((_, i) => ({ ...DEFAULT_BANNER, position: i + 1, section: SECTION_BY_INDEX[i] })),
  );
  // Any banner beyond the first 3 (by position) that ISN'T one of the known
  // named slots below (those get their own dedicated card instead) - this
  // list is for genuinely ad hoc banners an admin adds manually.
  const [extraBanners, setExtraBanners] = useState<IBanner[]>([]);
  // One entry per SCREEN_BANNER_GROUPS slot, keyed by section - the actual
  // saved banner if one exists yet, else a blank placeholder ready to upload.
  const [screenBanners, setScreenBanners] = useState<Record<string, IBanner>>({});
  const [screenBannerDirty, setScreenBannerDirty] = useState<Record<string, boolean>>({});
  // Destination/data are rarely needed for these mostly-decorative slots, so
  // they stay tucked behind a per-card "Advanced" toggle instead of always
  // taking up space.
  const [screenBannerExpanded, setScreenBannerExpanded] = useState<Record<string, boolean>>({});
  const [dirtyFlags, setDirtyFlags] = useState<boolean[]>(Array(3).fill(false));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<number | null>(null);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  // ── Extra banners (position 4+) dialog state ──────────────────────────
  const [extraDialogOpen, setExtraDialogOpen] = useState(false);
  const [editingExtraId, setEditingExtraId] = useState<string | null>(null);
  const [extraForm, setExtraForm] = useState<IBanner>(emptyExtraBanner(4));
  const [extraSaving, setExtraSaving] = useState(false);
  const [extraDeleteTarget, setExtraDeleteTarget] = useState<IBanner | null>(null);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const fetchedBanners = await fetchBanners();

      // Matched by `section` (TOP/MIDDLE/BOTTOM), not "first 3 by position" -
      // position-based slicing broke down once every named slot below also
      // needed a position value (see NAMED_SLOT_POSITIONS), since a primary
      // slot with no banner saved yet would let a named-slot banner slide
      // into its place in the naive top-3 slice.
      const primary = SECTION_BY_INDEX.map((sec, i) => {
        const existing = fetchedBanners.find((b) => b.section === sec);
        return existing || { ...DEFAULT_BANNER, position: i + 1, section: sec };
      });
      setBanners(primary);

      const allSlotSections = SCREEN_BANNER_GROUPS.flatMap((g) => g.slots);
      const nextScreenBanners: Record<string, IBanner> = {};
      allSlotSections.forEach((slot) => {
        nextScreenBanners[slot.section] =
          fetchedBanners.find((b) => b.section === slot.section) ||
          emptyNamedSlotBanner(slot.section);
      });
      setScreenBanners(nextScreenBanners);
      setScreenBannerDirty({});

      setExtraBanners(
        fetchedBanners.filter(
          (b) =>
            !SECTION_BY_INDEX.includes(b.section as 'TOP' | 'MIDDLE' | 'BOTTOM') &&
            !NAMED_SLOT_SECTIONS.has(b.section || ''),
        ),
      );
    } catch (error) {
      setError('Error fetching banners');
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerChange = async (
    index: number,
    field: keyof IBanner,
    value: string,
  ) => {
    const updatedBanners: IBanner[] = [...banners];
    updatedBanners[index] = {
      ...updatedBanners[index],
      [field]: value,
      position: index + 1,
      section: SECTION_BY_INDEX[index],
    };
    setBanners(updatedBanners);
    const updatedDirtyFlags = [...dirtyFlags];
    updatedDirtyFlags[index] = true;
    setDirtyFlags(updatedDirtyFlags);
  };

  const handleRemoveBanner = async (index: number) => {
    try {
      if (banners[index]?._id) {
        await deleteBanner(banners[index]._id as string);
      }
      setBanners((prevBanners) => {
        const newBanners = [...prevBanners];
        newBanners[index] = {
          ...DEFAULT_BANNER,
          position: index + 1,
          section: SECTION_BY_INDEX[index],
        };
        return newBanners;
      });
      const updatedDirtyFlags = [...dirtyFlags];
      updatedDirtyFlags[index] = false;
      setDirtyFlags(updatedDirtyFlags);
    } catch (error) {
      setError('Error deleting banner');
      console.error('Error deleting banner:', error);
    }
  };

  const handleSaveChanges = async (index: number) => {
    const banner = banners[index];
    try {
      if (!banner.destination || !banner.mediaUrl || !banner.position) {
        return showSnackbar('Image and destination are required', 'error');
      }
      setLoading(true);
      if (banner._id) {
        await editBanner({
          id: banner._id,
          appType: banner.appType,
          mediaType: banner.mediaType,
          mediaUrl: banner.mediaUrl,
          thumbnailUrl: banner.thumbnailUrl,
          section: banner.section,
          order: banner.order,
          destination: banner.destination,
          position: banner.position,
          data: banner.data,
        });
      } else {
        const savedBanner = await saveBanner({
          appType: banner.appType,
          mediaType: banner.mediaType,
          mediaUrl: banner.mediaUrl,
          thumbnailUrl: banner.thumbnailUrl,
          section: banner.section,
          order: banner.order,
          destination: banner.destination,
          position: banner.position,
          data: banner.data,
        });
        banner._id = savedBanner.toString();
      }
      const updatedDirtyFlags = [...dirtyFlags];
      updatedDirtyFlags[index] = false;
      setDirtyFlags(updatedDirtyFlags);
      showSnackbar('Banner saved', 'success');
      loadBanners();
    } catch (error) {
      setError('Error saving banners');
      console.error('Error saving banners:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Named screen-banner slots (Home/Shop/Products) ─────────────────────
  // Persists a specific banner object directly (not read from state) so an
  // image upload can save itself immediately, in the same tick it completes,
  // without depending on a later render + a manual Save click. With 14+
  // independent cards on this page, requiring a manual Save per card was the
  // easy way to upload a bunch of images and only remember to click Save on
  // a few of them - they'd look "saved" locally but vanish on next reload.
  const persistScreenBanner = async (section: string, banner: IBanner) => {
    if (!banner?.mediaUrl) return;
    setLoading(true);
    try {
      const payload = {
        appType: banner.appType,
        mediaType: banner.mediaType,
        mediaUrl: banner.mediaUrl,
        thumbnailUrl: banner.thumbnailUrl,
        section: banner.section,
        order: banner.order,
        destination: banner.destination,
        position: banner.position,
        data: banner.data,
      };
      if (banner._id) {
        await editBanner({ id: banner._id, ...payload });
      } else {
        await saveBanner(payload);
      }
      setScreenBannerDirty((prev) => ({ ...prev, [section]: false }));
      showSnackbar('Banner saved', 'success');
      loadBanners();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to save banner', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Image upload/remove auto-save immediately - the one thing every one of
  // these cards actually needs saved. Destination/data (behind "Set
  // destination") still use handleScreenBannerChange + a manual Save, since
  // those are optional and rarely touched.
  const handleScreenBannerImageChange = (section: string, url: string) => {
    const merged: IBanner = {
      ...(screenBanners[section] || emptyNamedSlotBanner(section)),
      mediaUrl: url,
    };
    setScreenBanners((prev) => ({ ...prev, [section]: merged }));
    persistScreenBanner(section, merged);
  };

  const handleScreenBannerChange = (
    section: string,
    field: keyof IBanner,
    value: string,
  ) => {
    setScreenBanners((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setScreenBannerDirty((prev) => ({ ...prev, [section]: true }));
  };

  const handleScreenBannerSave = (section: string) =>
    persistScreenBanner(section, screenBanners[section]);

  const handleScreenBannerRemove = async (section: string) => {
    const banner = screenBanners[section];
    if (!banner?._id) {
      setScreenBanners((prev) => ({ ...prev, [section]: { ...prev[section], mediaUrl: '' } }));
      return;
    }
    try {
      await deleteBanner(banner._id as string);
      showSnackbar('Banner removed', 'success');
      loadBanners();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to remove banner', 'error');
    }
  };

  const openDeleteDialog = (index: number) => {
    setBannerToDelete(index);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBannerToDelete(null);
  };

  // ── Extra banners (position 4+) handlers ──────────────────────────────
  const openCreateExtra = () => {
    const nextPosition =
      extraBanners.reduce((max, b) => Math.max(max, b.position || 0), 3) + 1;
    setEditingExtraId(null);
    setExtraForm(emptyExtraBanner(nextPosition));
    setExtraDialogOpen(true);
  };

  const openEditExtra = (banner: IBanner) => {
    setEditingExtraId(banner._id || null);
    setExtraForm({ ...banner });
    setExtraDialogOpen(true);
  };

  const handleExtraSave = async () => {
    if (!extraForm.mediaUrl || !extraForm.destination || !extraForm.position) {
      showSnackbar('Image, destination and position are required', 'error');
      return;
    }
    setExtraSaving(true);
    try {
      if (editingExtraId) {
        await editBanner({
          id: editingExtraId,
          appType: extraForm.appType,
          mediaType: extraForm.mediaType,
          mediaUrl: extraForm.mediaUrl,
          thumbnailUrl: extraForm.thumbnailUrl,
          section: extraForm.section,
          order: extraForm.order,
          destination: extraForm.destination,
          position: extraForm.position,
          data: extraForm.data,
        });
        showSnackbar('Banner updated', 'success');
      } else {
        await saveBanner({
          appType: extraForm.appType,
          mediaType: extraForm.mediaType,
          mediaUrl: extraForm.mediaUrl,
          thumbnailUrl: extraForm.thumbnailUrl,
          section: extraForm.section,
          order: extraForm.order,
          destination: extraForm.destination,
          position: extraForm.position,
          data: extraForm.data,
        });
        showSnackbar('Banner created', 'success');
      }
      setExtraDialogOpen(false);
      loadBanners();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to save banner', 'error');
    } finally {
      setExtraSaving(false);
    }
  };

  const handleExtraDelete = async () => {
    if (!extraDeleteTarget?._id) return;
    try {
      await deleteBanner(extraDeleteTarget._id);
      showSnackbar('Banner deleted', 'success');
      setExtraDeleteTarget(null);
      loadBanners();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to delete banner', 'error');
    }
  };

  const filledBanners = banners.filter((b) => b.mediaUrl).length;

  return (
    <Box sx={{ p: 3 }}>
      {SnackbarComponent}

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DARK})`,
            boxShadow: '0 6px 20px rgba(123,31,162,0.45)',
            flexShrink: 0,
          }}
        >
          <CollectionsIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography
            sx={{ color: '#fff', fontWeight: 700, fontSize: 24, letterSpacing: -0.3 }}
          >
            Banner Studio
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 14 }}>
            Every promo banner across the app — Home, Shop, Products — managed
            from one place.
          </Typography>
        </Box>
      </Box>

      {/* ── Banners gallery ─────────────────────────────────────────── */}
      <SectionCard>
        <SectionHeader
          icon={<CollectionsIcon />}
          title="Promotional Banners"
          subtitle="Up to 3 banners shown in the app home carousel"
          action={
            <Chip
              label={`${filledBanners} / 3 set`}
              size="small"
              sx={{ bgcolor: 'rgba(123,31,162,0.16)', color: PURPLE, fontWeight: 600 }}
            />
          }
        />

        <Grid container spacing={2.5}>
          {banners.map((banner, index) => {
            const isSet = Boolean(banner.mediaUrl);
            return (
              <Grid item xs={12} sm={6} md={4} key={banner._id || index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#171717',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    backgroundImage: 'none',
                    transition: 'transform .18s ease, box-shadow .18s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, p: 1.75 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                        {PRIMARY_BANNER_LABELS[index]}
                      </Typography>
                      <Chip
                        label={isSet ? 'Active' : 'Empty'}
                        size="small"
                        sx={{
                          bgcolor: isSet ? 'rgba(11,230,0,0.14)' : 'rgba(138,141,145,0.14)',
                          color: isSet ? '#0BE600' : MUTED,
                          fontWeight: 600,
                          height: 22,
                        }}
                      />
                    </Box>

                    <ImageUpload
                      variant="tile"
                      aspectRatio={16 / 9}
                      fileId={`banner-${index}`}
                      type="banner"
                      imageUrl={banner.mediaUrl}
                      onChange={(url) => handleBannerChange(index, 'mediaUrl', url)}
                      onRemove={() => openDeleteDialog(index)}
                      uploadLabel="Upload banner"
                      setLoader={setLoading}
                      disabled={loading}
                      folderType={BANNER_FOLDER_TYPE}
                    />
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Destination</InputLabel>
                      <Select
                        label="Destination"
                        value={banner.destination}
                        onChange={(e) =>
                          handleBannerChange(
                            index,
                            'destination',
                            e.target.value as IBanner['destination'],
                          )
                        }
                      >
                        {DESTINATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label={
                        banner.destination === 'COUPON'
                          ? 'Coupon code / link'
                          : 'Destination link / data'
                      }
                      variant="outlined"
                      margin="dense"
                      value={banner.data || ''}
                      onChange={(e) => handleBannerChange(index, 'data', e.target.value)}
                    />
                  </CardContent>

                  <CardActions sx={{ px: 1.75, pb: 1.75, pt: 0, justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      onClick={() => openDeleteDialog(index)}
                      disabled={loading || !isSet}
                      color="error"
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSaveChanges(index)}
                      disabled={loading || !dirtyFlags[index]}
                      sx={{
                        bgcolor: PURPLE,
                        '&:hover': { bgcolor: '#5E167A' },
                        textTransform: 'none',
                        borderRadius: '10px',
                      }}
                    >
                      Save
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={26} sx={{ color: PURPLE }} />
          </Box>
        )}
      </SectionCard>

      {/* ── Named screen banners (Home/Shop/Products) ───────────────── */}
      <SectionCard>
        <SectionHeader
          icon={<ViewQuiltIcon />}
          title="Screen Banners"
          subtitle="Every other promo banner used across the app, grouped by screen"
        />

        {SCREEN_BANNER_GROUPS.map((group) => {
          const groupFilled = group.slots.filter(
            (slot) => screenBanners[slot.section]?.mediaUrl,
          ).length;
          return (
            <Box key={group.title} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group.title}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(groupFilled / group.slots.length) * 100}
                  sx={{
                    flex: 1,
                    height: 5,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: `linear-gradient(90deg, ${PURPLE_DARK}, ${PURPLE})`,
                    },
                  }}
                />
                <Typography sx={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>
                  {groupFilled}/{group.slots.length}
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {group.slots.map((slot) => {
                  const banner = screenBanners[slot.section] || emptyNamedSlotBanner(slot.section);
                  const isSet = Boolean(banner.mediaUrl);
                  const isDirty = Boolean(screenBannerDirty[slot.section]);
                  const isExpanded = Boolean(screenBannerExpanded[slot.section]);
                  return (
                    <Grid item xs={12} sm={6} md={3} key={slot.section}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: '#171717',
                          border: `1px solid ${BORDER}`,
                          borderRadius: 2.5,
                          backgroundImage: 'none',
                          transition: 'transform .18s ease, box-shadow .18s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 10px 24px rgba(0,0,0,0.3)',
                          },
                        }}
                      >
                        <CardContent sx={{ flex: 1, p: 1.25 }}>
                          <Box sx={{ position: 'relative' }}>
                            <ImageUpload
                              variant="tile"
                              aspectRatio={4 / 3}
                              fileId={`screen-banner-${slot.section}`}
                              type="icon"
                              imageUrl={banner.mediaUrl}
                              onChange={(url) => handleScreenBannerImageChange(slot.section, url)}
                              onRemove={() => handleScreenBannerRemove(slot.section)}
                              uploadLabel={slot.label}
                              setLoader={setLoading}
                              disabled={loading}
                              folderType={BANNER_FOLDER_TYPE}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                backdropFilter: 'blur(4px)',
                                px: 0.9,
                                py: 0.3,
                                borderRadius: 4,
                                pointerEvents: 'none',
                                maxWidth: 'calc(100% - 12px)',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  bgcolor: isSet ? '#0BE600' : '#8A8D91',
                                }}
                              />
                              <Typography
                                sx={{
                                  color: '#fff',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {slot.label}
                              </Typography>
                            </Box>
                          </Box>

                          {isExpanded && (
                            <>
                              <FormControl fullWidth margin="dense" size="small">
                                <InputLabel>Destination</InputLabel>
                                <Select
                                  label="Destination"
                                  value={banner.destination}
                                  onChange={(e) =>
                                    handleScreenBannerChange(
                                      slot.section,
                                      'destination',
                                      e.target.value as IBanner['destination'],
                                    )
                                  }
                                >
                                  {DESTINATION_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                fullWidth
                                size="small"
                                label={
                                  banner.destination === 'COUPON'
                                    ? 'Coupon code / link'
                                    : 'Destination link / data'
                                }
                                variant="outlined"
                                margin="dense"
                                value={banner.data || ''}
                                onChange={(e) =>
                                  handleScreenBannerChange(slot.section, 'data', e.target.value)
                                }
                              />
                            </>
                          )}

                          <Button
                            size="small"
                            startIcon={<TuneIcon sx={{ fontSize: 14 }} />}
                            onClick={() =>
                              setScreenBannerExpanded((prev) => ({
                                ...prev,
                                [slot.section]: !prev[slot.section],
                              }))
                            }
                            sx={{
                              mt: 0.5,
                              color: MUTED,
                              textTransform: 'none',
                              fontSize: 11,
                              minWidth: 0,
                              p: 0,
                              '&:hover': { bgcolor: 'transparent', color: PURPLE },
                            }}
                          >
                            {isExpanded ? 'Hide destination' : 'Set destination'}
                          </Button>
                        </CardContent>

                        <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleScreenBannerSave(slot.section)}
                            disabled={loading || !isDirty}
                            sx={{
                              bgcolor: PURPLE,
                              '&:hover': { bgcolor: '#5E167A' },
                              textTransform: 'none',
                              borderRadius: '8px',
                              fontSize: 12,
                            }}
                          >
                            Save
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}
      </SectionCard>

      {/* ── Additional banners (position 4+) ────────────────────────── */}
      <SectionCard>
        <SectionHeader
          icon={<CollectionsIcon />}
          title="Additional Banners"
          subtitle="Ad hoc banners not covered by a named slot above"
          action={
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateExtra}
              sx={{
                bgcolor: PURPLE,
                '&:hover': { bgcolor: '#5E167A' },
                textTransform: 'none',
                borderRadius: '10px',
              }}
            >
              Add Banner
            </Button>
          }
        />

        {extraBanners.length === 0 ? (
          <Typography sx={{ color: MUTED, textAlign: 'center', py: 4 }}>
            No additional banners yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {extraBanners.map((banner) => (
              <Grid item xs={12} sm={6} md={4} key={banner._id}>
                <Paper
                  sx={{
                    bgcolor: SURFACE_ALT,
                    border: '1px solid #2F2F2F',
                    borderRadius: 3,
                    p: 1.5,
                    backgroundImage: 'none',
                    transition: 'transform .18s ease, box-shadow .18s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: 2,
                        overflow: 'hidden',
                        flexShrink: 0,
                        bgcolor: '#2E2E2E',
                        border: '1px solid #3E3E3E',
                      }}
                    >
                      {banner.mediaUrl && (
                        <img
                          src={banner.mediaUrl}
                          alt={banner.destination}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip
                          label={banner.appType}
                          size="small"
                          sx={{ bgcolor: 'rgba(123,31,162,0.16)', color: PURPLE, height: 20 }}
                        />
                        <Chip
                          label={`pos ${banner.position}`}
                          size="small"
                          sx={{ bgcolor: 'rgba(138,141,145,0.14)', color: MUTED, height: 20 }}
                        />
                      </Box>
                      <Typography sx={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>
                        {banner.section ? `${banner.section} · ` : ''}
                        {banner.destination}
                      </Typography>
                      {banner.data && (
                        <Typography
                          sx={{
                            color: MUTED,
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {banner.data}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                    <IconButton size="small" onClick={() => openEditExtra(banner)} sx={{ color: PURPLE }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setExtraDeleteTarget(banner)}
                      sx={{ color: '#E31E25' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </SectionCard>

      {/* ── Home screen layout / config ─────────────────────────────── */}
      <SectionCard sx={{ mb: 0 }}>
        <SectionHeader
          icon={<ViewQuiltIcon />}
          title="Home Screen Layout"
          subtitle="Control which sections appear and their order, per app screen"
        />
        <HomeConfigManager banners={banners} onBannersChanged={loadBanners} />
      </SectionCard>

      {error && (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          message={error}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: {
            bgcolor: '#171717',
            color: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 3,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle>Remove this banner?</DialogTitle>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDeleteDialog} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (bannerToDelete !== null) handleRemoveBanner(bannerToDelete);
              closeDeleteDialog();
            }}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / edit dialog for additional (position 4+) banners */}
      <Dialog
        open={extraDialogOpen}
        onClose={() => !extraSaving && setExtraDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: SURFACE, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>
          {editingExtraId ? 'Edit Banner' : 'Add Banner'}
        </DialogTitle>
        <Divider sx={{ borderColor: '#3E3E3E' }} />
        <DialogContent sx={{ backgroundColor: SURFACE }}>
          <Box sx={{ mt: 1 }}>
            <ImageUpload
              fileId="extra-banner-upload"
              type="banner"
              imageUrl={extraForm.mediaUrl}
              onChange={(url) => setExtraForm((p) => ({ ...p, mediaUrl: url }))}
              onRemove={() => setExtraForm((p) => ({ ...p, mediaUrl: '' }))}
              uploadLabel="Upload Banner"
              disabled={extraSaving}
              folderType={BANNER_FOLDER_TYPE}
            />
          </Box>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="App Type"
                value={extraForm.appType}
                onChange={(e) =>
                  setExtraForm((p) => ({ ...p, appType: e.target.value as IBanner['appType'] }))
                }
              >
                {(['USER', 'TECHNICIAN', 'CONTRACTOR'] as const).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Section"
                value={extraForm.section || ''}
                onChange={(e) =>
                  setExtraForm((p) => ({ ...p, section: e.target.value as IBanner['section'] }))
                }
              >
                {KNOWN_BANNER_SECTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Destination"
                value={extraForm.destination}
                onChange={(e) =>
                  setExtraForm((p) => ({
                    ...p,
                    destination: e.target.value as IBanner['destination'],
                  }))
                }
              >
                {DESTINATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Position"
                value={extraForm.position}
                onChange={(e) =>
                  setExtraForm((p) => ({ ...p, position: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Order"
                value={extraForm.order ?? ''}
                onChange={(e) =>
                  setExtraForm((p) => ({ ...p, order: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={
                  extraForm.destination === 'COUPON' ? 'Coupon code / link' : 'Destination link / data'
                }
                value={extraForm.data || ''}
                onChange={(e) => setExtraForm((p) => ({ ...p, data: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider sx={{ borderColor: '#3E3E3E' }} />
        <DialogActions sx={{ backgroundColor: SURFACE, p: 2 }}>
          <Button
            onClick={() => setExtraDialogOpen(false)}
            disabled={extraSaving}
            sx={{ color: MUTED, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExtraSave}
            disabled={extraSaving}
            sx={{
              backgroundColor: PURPLE,
              '&:hover': { backgroundColor: '#5E167A' },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            {extraSaving ? 'Saving…' : editingExtraId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm for additional banners */}
      <Dialog
        open={Boolean(extraDeleteTarget)}
        onClose={() => setExtraDeleteTarget(null)}
        PaperProps={{ sx: { backgroundColor: SURFACE, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: MUTED }}>
            Delete this {extraDeleteTarget?.appType} · {extraDeleteTarget?.destination}{' '}
            banner? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExtraDeleteTarget(null)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExtraDelete}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(HomeBanner);
